import QRCodeLib from 'qrcode';
import { jsPDF } from 'jspdf';

const ORDER_TYPES = [
  { value: 'dinein', label: 'Dine-in' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'not_required', label: 'Not Required' },
  { value: 'optional', label: 'Optional' },
];

/**
 * Generate a batch PDF with multiple table QR codes
 * @param {Object} params - Configuration parameters
 * @param {number} params.startTableNumber - Starting table number
 * @param {number} params.endTableNumber - Ending table number
 * @param {string} params.editOrderType - Order type for the QR codes
 * @param {string} params.editPayment - Payment setting
 * @param {string} params.editPosAcceptOrders - POS acceptance setting
 * @param {Function} params.generateQrCodeUrl - Function to generate QR code URL
 * @param {Function} params.createEnhancedShortUrl - Function to create short URL
 * @param {Map} params.shortUrlCache - Cache for short URLs
 * @param {Function} params.setPdfProgress - Progress update callback
 * @param {Object} params.restaurant - Restaurant object
 * @returns {Promise<Map>} Updated shortUrlCache
 */
export const generateBatchPDF = async ({
  startTableNumber,
  endTableNumber,
  editOrderType,
  editPayment,
  editPosAcceptOrders,
  generateQrCodeUrl,
  createEnhancedShortUrl,
  shortUrlCache,
  setPdfProgress,
  restaurant,
}) => {
  // Create PDF with A4 size
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // QR code settings for PDF
  const qrSize = 60; // mm
  const marginX = 20;
  const marginY = 20;
  const spacing = 10;
  const labelHeight = 15;

  // Calculate how many QR codes fit per row and column
  const codesPerRow = Math.floor((pageWidth - 2 * marginX + spacing) / (qrSize + spacing));
  const codesPerCol = Math.floor((pageHeight - 2 * marginY + spacing) / (qrSize + labelHeight + spacing));
  const codesPerPage = codesPerRow * codesPerCol;

  const totalTables = endTableNumber - startTableNumber + 1;
  const currentTable = startTableNumber;

  // Helper function to generate QR code image data
  const generateQrCodeImage = async (url) => {
    try {
      const dataUrl = await QRCodeLib.toDataURL(url, {
        width: 200,
        margin: 1,
        errorCorrectionLevel: 'H',
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });
      return dataUrl;
    } catch (err) {
      console.error('Error generating QR image:', err);
      return null;
    }
  };

  const updatedCache = new Map(shortUrlCache);

  for (let i = 0; i < totalTables; i++) {
    const tableNum = currentTable + i;
    const positionOnPage = i % codesPerPage;

    // Add new page if needed (except for the first one)
    if (positionOnPage === 0 && i > 0) {
      pdf.addPage();
    }

    // Calculate position on current page
    const row = Math.floor(positionOnPage / codesPerRow);
    const col = positionOnPage % codesPerRow;
    const x = marginX + col * (qrSize + spacing);
    const y = marginY + row * (qrSize + labelHeight + spacing);

    // Generate QR code URL for this table
    let qrUrl = generateQrCodeUrl(tableNum.toString());
    
    // Try to create a short URL for this table
    try {
      const cacheKey = `${tableNum}_${editOrderType}_${editPayment}_${editPosAcceptOrders}`;
      
      // Check if we already have a short URL for this configuration
      if (!updatedCache.has(cacheKey)) {
        const shortUrlResult = await createEnhancedShortUrl({
          orderType: editOrderType,
          payment: editPayment,
          posAcceptOrders: editPosAcceptOrders,
          tableNumber: tableNum.toString(),
          expiresTimestamp: null,
        });
        if (shortUrlResult && shortUrlResult.shortUrl) {
          updatedCache.set(cacheKey, shortUrlResult.shortUrl);
          qrUrl = shortUrlResult.shortUrl;
        }
      } else {
        qrUrl = updatedCache.get(cacheKey);
      }
    } catch (error) {
      console.warn(`Failed to create short URL for table ${tableNum}, using long URL`);
    }

    // Create QR code using canvas-based approach
    const imgData = await generateQrCodeImage(qrUrl);

    // Add QR code image to PDF
    if (imgData) {
      pdf.addImage(imgData, 'PNG', x, y, qrSize, qrSize);
    }

    // Add table label below QR code
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Table ${tableNum}`, x + qrSize / 2, y + qrSize + 8, { align: 'center' });

    // Update progress
    setPdfProgress(Math.round(((i + 1) / totalTables) * 100));
  }

  // Add footer with restaurant name and settings on each page
  const totalPages = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(128);

    const footerText = `${restaurant?.name || 'Restaurant'} | Order: ${ORDER_TYPES.find(o => o.value === editOrderType)?.label} | Payment: ${PAYMENT_OPTIONS.find(o => o.value === editPayment)?.label} | Page ${p} of ${totalPages}`;
    pdf.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  // Save the PDF
  pdf.save(`${restaurant?.urlIdentifier}-tables-${startTableNumber}-to-${endTableNumber}.pdf`);

  return updatedCache;
};
